import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';

import { LocaleService } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { SiteActions } from '@next/core-lib/site';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { ProductionPurposeCategoryDetailed, SiteState } from '../shared/models';
import {
  DeleteProductionPurposeCategory,
  GetProductionPurposeCategoriesDetailed,
  OpenCreateProductionPurposeCategoriesDialog,
  OpenEditProductionPurposeCategoryDialog,
} from '../store/actions';
import { selectProductionPurposeCategoriesDetailed } from '../store/selectors';

enum ProductionPurposeCategoryAction {
  edit = 'edit',
  remove = 'remove',
}

@Component({
  selector: 'app-production-purpose-category',
  templateUrl: './production-purpose-category.component.html',
  styleUrl: './production-purpose-category.component.scss',
  standalone: false,
})
export class ProductionPurposeCategoryComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);

  private readonly subscription = new Subscription();
  title = this.locale.translate('productionPurposeCategory.title');

  displayedColumns: string[] = [
    'name',
    'description',
    'n1',
    'n2',
    'n3',
    'baseYieldPotential',
    'nCorrection',
    'nO3NormTreshold',
    'trendlineTuningA',
    'trendlineTuningB',
    'trendlineTuningC',
    'trendlineTuningD',
    'actions',
  ];

  productionPurposeCategoryActions = [
    {
      icon: 'edit',
      viewValue: this.locale.translate('edit'),
      key: ProductionPurposeCategoryAction.edit,
    },
    {
      icon: 'delete',
      viewValue: this.locale.translate('remove'),
      key: ProductionPurposeCategoryAction.remove,
    },
  ];
  dataSource = new MatTableDataSource<ProductionPurposeCategoryDetailed>();
  dataAvailable = false;
  isMobile$ = this.breakpoint.ltLg$;

  ngOnInit(): void {
    this.store.dispatch(SiteActions.updatePageTitle({ pageTitle: this.title }));
    this.store.dispatch(new GetProductionPurposeCategoriesDetailed());

    const selectProductionPurposeCategories = this.store
      .pipe(select(selectProductionPurposeCategoriesDetailed))
      .subscribe((result) => {
        this.dataSource.data = result;
        this.dataAvailable = result.length > 0;
        this.cdr.detectChanges();
      });

    this.subscription.add(selectProductionPurposeCategories);
    this.updateData();
  }

  updateData() {
    this.store.dispatch(new GetProductionPurposeCategoriesDetailed());
  }
  onPage() {
    this.updateData();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  createProductionPurposeCategory() {
    this.store.dispatch(new OpenCreateProductionPurposeCategoriesDialog());
  }

  productionPurposeCategoryAction(key: string, id: number) {
    switch (key) {
      case ProductionPurposeCategoryAction.edit:
        this.editProductionPurposeCategory(id);
        break;
      case ProductionPurposeCategoryAction.remove:
        this.removeProductionPurposeCategory(id);
        break;
    }
  }

  editProductionPurposeCategory(id: number) {
    const productionPurposeCategory = this.dataSource.data.find((np) => np.id === id);
    this.store.dispatch(new OpenEditProductionPurposeCategoryDialog({ productionPurposeCategory }));
  }

  removeProductionPurposeCategory(id: number) {
    this.store.dispatch(new DeleteProductionPurposeCategory(id));
  }
}
